import { useMemo, useRef, Suspense } from 'react';
import { Canvas, useFrame } from '@react-three/fiber';
import { Line, Html, Float } from '@react-three/drei';
import * as THREE from 'three';
import type { ImportOrderLeg, TransportMode } from '../types/import-tracking.types';

/**
 * La ruta del embarque, en tres dimensiones.
 *
 * **Por qué.** El recorrido se mostraba como dos tarjetas de texto apiladas:
 * Shanghái → Arica, Arica → Santa Cruz. Eso es una lista, no un viaje. Nadie
 * puede mirar dos rectángulos y decir dónde está la carga, cuánto falta, ni
 * cuál de los dos tramos es el largo.
 *
 * Acá el trayecto es un arco entre puntos, con el vehículo de cada modo
 * avanzando sobre él. La posición del vehículo es el estado real del tramo
 * —pendiente al inicio, en curso a la mitad, completado al final— así que la
 * escena **se lee de un vistazo** y no hay que sumar leyendas mentalmente.
 *
 * Lo que se dibuja sale de los datos, no de una animación decorativa: un tramo
 * cancelado no tiene vehículo, uno demorado se pinta en rojo, y la distancia
 * entre nodos es proporcional al costo del tramo — el tramo caro se ve largo.
 */

interface RouteJourney3DProps {
  legs: ImportOrderLeg[];
  /** Alto del lienzo. Por defecto 240 px. */
  height?: number;
  className?: string;
}

/** Colores por modo, en el mismo lenguaje que las tarjetas de abajo. */
const MODE_COLOR: Record<TransportMode, string> = {
  SEA: '#0ea5e9',
  AIR: '#6366f1',
  LAND: '#f59e0b',
  RAIL: '#64748b',
  MULTIMODAL: '#a855f7',
};

/** Dónde está el vehículo dentro de su tramo, según el estado. */
function avanceDe(status: ImportOrderLeg['status']): number {
  if (status === 'COMPLETED') return 1;
  if (status === 'IN_PROGRESS') return 0.5;
  return 0;
}

interface Nodo {
  nombre: string;
  x: number;
}

interface Tramo {
  leg: ImportOrderLeg;
  desde: number;
  hasta: number;
  color: string;
  avance: number;
}

/**
 * Reparte los tramos sobre el eje X.
 *
 * El ancho de cada uno es proporcional a su costo, con un mínimo para que un
 * tramo barato no quede invisible. Es información: en un embarque real el
 * cruce del Pacífico y el último tramo en camión no cuestan lo mismo, y verlo
 * del mismo largo esconde dónde se va la plata.
 */
function distribuir(legs: ImportOrderLeg[]): { nodos: Nodo[]; tramos: Tramo[]; ancho: number } {
  const ANCHO_MIN = 0.6;
  const ESCALA = 6;

  const costos = legs.map((l) => Math.max(0, Number(l.calculatedAmount ?? 0)));
  const totalCosto = costos.reduce((s, c) => s + c, 0);

  const anchos = legs.map((_, i) =>
    totalCosto > 0
      ? ANCHO_MIN + (costos[i] / totalCosto) * ESCALA
      : ANCHO_MIN + ESCALA / legs.length
  );

  const total = anchos.reduce((s, a) => s + a, 0);
  // Centrado en el origen para que la cámara no tenga que moverse.
  let x = -total / 2;

  const nodos: Nodo[] = [{ nombre: legs[0].origin, x }];
  const tramos: Tramo[] = [];

  legs.forEach((leg, i) => {
    const desde = x;
    x += anchos[i];
    tramos.push({
      leg,
      desde,
      hasta: x,
      color: leg.status === 'DELAYED' ? '#ef4444' : (MODE_COLOR[leg.transportMode] ?? '#0ea5e9'),
      avance: avanceDe(leg.status),
    });
    nodos.push({ nombre: leg.destination, x });
  });

  return { nodos, tramos, ancho: total };
}

/** La curva de un tramo: un arco suave, no una recta. */
function curvaDe(desde: number, hasta: number, alto: number): THREE.CatmullRomCurve3 {
  const medio = (desde + hasta) / 2;
  return new THREE.CatmullRomCurve3([
    new THREE.Vector3(desde, 0, 0),
    new THREE.Vector3(medio, alto, 0),
    new THREE.Vector3(hasta, 0, 0),
  ]);
}

/** El trazo del tramo, con la parte recorrida en color pleno. */
function TrazoTramo({ tramo }: { tramo: Tramo }) {
  const { recorrido, pendiente } = useMemo(() => {
    const curva = curvaDe(tramo.desde, tramo.hasta, 0.9);
    const puntos = curva.getPoints(48);
    const corte = Math.max(1, Math.round(puntos.length * tramo.avance));
    return {
      recorrido: puntos.slice(0, corte + 1),
      pendiente: puntos.slice(corte),
    };
  }, [tramo]);

  return (
    <group>
      {pendiente.length > 1 && (
        <Line
          points={pendiente}
          color="#cbd5e1"
          lineWidth={2}
          dashed
          dashSize={0.12}
          gapSize={0.1}
        />
      )}
      {recorrido.length > 1 && <Line points={recorrido} color={tramo.color} lineWidth={4} />}
    </group>
  );
}

/**
 * El vehículo del tramo.
 *
 * Se mueve sobre su arco con un vaivén corto alrededor del punto que marca el
 * estado: quieto en el extremo si está pendiente o completado, oscilando en el
 * medio mientras está en curso. La animación indica actividad, no adorna.
 */
function Vehiculo({ tramo }: { tramo: Tramo }) {
  const ref = useRef<THREE.Group>(null);
  const curva = useMemo(() => curvaDe(tramo.desde, tramo.hasta, 0.9), [tramo]);
  const enCurso = tramo.leg.status === 'IN_PROGRESS';

  useFrame((state) => {
    if (!ref.current) return;
    const t = enCurso ? 0.5 + Math.sin(state.clock.elapsedTime * 0.8) * 0.22 : tramo.avance;
    const p = curva.getPointAt(Math.min(1, Math.max(0, t)));
    const tangente = curva.getTangentAt(Math.min(0.999, Math.max(0.001, t)));
    ref.current.position.set(p.x, p.y + 0.16, p.z);
    ref.current.rotation.z = Math.atan2(tangente.y, tangente.x) * 0.5;
  });

  if (tramo.leg.status === 'CANCELLED') return null;

  return (
    <group ref={ref}>
      <Float speed={enCurso ? 3 : 0} floatIntensity={enCurso ? 0.3 : 0} rotationIntensity={0}>
        <FormaDelModo modo={tramo.leg.transportMode} color={tramo.color} />
      </Float>
    </group>
  );
}

/**
 * La silueta de cada medio de transporte.
 *
 * Geometría primitiva a propósito: se lee a 240 px de alto, pesa nada y no
 * necesita cargar modelos externos —que además la CSP de la app bloquearía.
 */
function FormaDelModo({ modo, color }: { modo: TransportMode; color: string }) {
  const material = <meshStandardMaterial color={color} roughness={0.35} metalness={0.25} />;

  if (modo === 'AIR') {
    return (
      <group rotation={[0, 0, -0.15]}>
        <mesh>
          <capsuleGeometry args={[0.055, 0.26, 4, 12]} />
          {material}
        </mesh>
        {/* Las alas */}
        <mesh rotation={[Math.PI / 2, 0, 0]} position={[0, 0, 0]}>
          <boxGeometry args={[0.05, 0.34, 0.02]} />
          {material}
        </mesh>
      </group>
    );
  }

  if (modo === 'LAND' || modo === 'RAIL') {
    return (
      <group>
        {/* La caja */}
        <mesh position={[0.03, 0.02, 0]}>
          <boxGeometry args={[0.3, 0.16, 0.14]} />
          {material}
        </mesh>
        {/* La cabina */}
        <mesh position={[-0.16, 0, 0]}>
          <boxGeometry args={[0.12, 0.12, 0.13]} />
          <meshStandardMaterial color="#334155" roughness={0.4} />
        </mesh>
      </group>
    );
  }

  // Marítimo y multimodal: casco con contenedores encima.
  return (
    <group>
      <mesh position={[0, -0.02, 0]}>
        <boxGeometry args={[0.4, 0.1, 0.15]} />
        {material}
      </mesh>
      {[-0.1, 0, 0.1].map((x, i) => (
        <mesh key={x} position={[x, 0.07, 0]}>
          <boxGeometry args={[0.08, 0.07, 0.11]} />
          <meshStandardMaterial color={i === 1 ? '#f8fafc' : '#e2e8f0'} roughness={0.6} />
        </mesh>
      ))}
    </group>
  );
}

/** Un puerto o ciudad: el pilar y el nombre. */
function NodoRuta({ nodo, alcanzado }: { nodo: Nodo; alcanzado: boolean }) {
  return (
    <group position={[nodo.x, 0, 0]}>
      <mesh position={[0, -0.06, 0]}>
        <cylinderGeometry args={[0.06, 0.06, 0.12, 16]} />
        <meshStandardMaterial
          color={alcanzado ? '#10b981' : '#94a3b8'}
          emissive={alcanzado ? '#10b981' : '#000000'}
          emissiveIntensity={alcanzado ? 0.35 : 0}
        />
      </mesh>
      {/*
        El rótulo va en HTML, no con `<Text>` de drei.

        🔴 `<Text>` se veía bien pero pedía la tipografía a **jsdelivr**: cuatro
        peticiones a un CDN externo cada vez que alguien abre una orden. Lo
        verifiqué en el navegador —`unicode-font-resolver` + dos `.woff`— y es el
        mismo problema que ya se había sacado del portal del cliente; acá había
        quedado.

        Además de la dependencia externa, `<Text>` es geometría 3D: se encoge con
        la escena y se corta. En HTML usa la tipografía de la página, se ve nítido
        en cualquier pantalla y no depende de que un servidor ajeno responda.
      */}
      <Html position={[0, -0.42, 0]} center zIndexRange={[5, 0]}>
        <div
          className="pointer-events-none w-[110px] text-center text-[11px] font-semibold leading-tight"
          style={{ color: alcanzado ? '#065f46' : '#475569' }}
        >
          {nodo.nombre}
        </div>
      </Html>
    </group>
  );
}

/** La escena entera, sin el lienzo. Separada para poder testear el reparto. */
function Escena({ legs }: { legs: ImportOrderLeg[] }) {
  const { nodos, tramos } = useMemo(() => distribuir(legs), [legs]);

  // Un nodo está alcanzado si el tramo que llega a él se completó.
  const alcanzado = (i: number) => i === 0 || tramos[i - 1]?.leg.status === 'COMPLETED';

  return (
    <>
      <ambientLight intensity={0.85} />
      <directionalLight position={[3, 5, 4]} intensity={1.1} />
      <directionalLight position={[-4, 2, -2]} intensity={0.35} />

      {tramos.map((t) => (
        <group key={t.leg.id}>
          <TrazoTramo tramo={t} />
          <Vehiculo tramo={t} />
        </group>
      ))}

      {nodos.map((n, i) => (
        <NodoRuta key={`${n.nombre}-${n.x}`} nodo={n} alcanzado={alcanzado(i)} />
      ))}
    </>
  );
}

export function RouteJourney3D({ legs, height = 240, className }: RouteJourney3DProps) {
  if (legs.length === 0) return null;

  /*
    La cámara se aleja según cuántos tramos haya, para que una ruta de cinco
    tramos no se salga del lienzo ni una de uno quede diminuta.
  */
  const distancia = 4.2 + legs.length * 1.15;

  return (
    <div className={className} style={{ height }} aria-hidden>
      {/*
        `aria-hidden`: es una representación de datos que ya están escritos en
        las tarjetas de abajo, con su origen, destino, estado y costo. Para
        alguien que navega con lector de pantalla, leer la escena sería repetir
        lo mismo sin agregar nada.
      */}
      <Canvas
        camera={{ position: [0, 1.1, distancia], fov: 42 }}
        dpr={[1, 2]}
        gl={{ antialias: true, alpha: true }}
      >
        <Suspense fallback={null}>
          <Escena legs={legs} />
        </Suspense>
      </Canvas>
    </div>
  );
}

export { distribuir as __distribuir };
